import { useState } from 'react';
import { Card, Group, List, Stack, Text, Title, Alert, Loader, Center } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle } from 'react-feather';
import { matchingSessionsApi } from '../../../../services/api/matchingSessions';
import { MatchingSessionRow } from './MatchingSessionRow';
import { SetUpMatchingButton } from './SetUpMatchingButton';
import { StartMatchingButton } from './StartMatchingButton';

const POLL_INTERVAL_MS = 30_000;

/**
 * User-actionable error messages for Resume failures keyed by the medley-api
 * `code` field. Falls back to the apiClient-enhanced `message` if the code
 * isn't mapped.
 *
 * Resume is STAFF-only as of medley-api PR #61. Any staff user may resume any
 * matching session, so the old `NOT_OWNER` code no longer exists. A non-staff
 * caller now gets a 403 with no `code` field and falls back to `message`.
 */
const RESUME_ERROR_CODE_MESSAGES: Record<string, string> = {
  MATCHING_SESSION_NOT_FOUND: 'This matching session no longer exists.',
  MATCHING_SESSION_EXPIRED:
    'This matching session has expired. Click Start Matching to begin a new one.',
  MATCHING_SESSION_STILL_CREATING:
    "The session is still being created — wait a moment, then try again.",
  MATCHING_SESSION_FAILED_NO_EXTERNAL_SESSION:
    'This session failed before reaching the matching server. Click Start Matching to begin a new one.',
  MATCHING_SERVER_AUTH_REJECTED:
    'Authentication with the matching service failed. Contact support.',
  MATCHING_SERVER_UNREACHABLE:
    "Couldn't reach the matching service. Try again in a moment.",
  MATCHING_SERVER_INVALID_RESPONSE:
    'The matching service returned an unexpected response. Contact support.',
  MATCHING_SERVER_NOT_CONFIGURED:
    'The matching service is not configured for this environment. Contact support.',
};

function deriveResumeErrorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string };
  if (e?.code && RESUME_ERROR_CODE_MESSAGES[e.code]) return RESUME_ERROR_CODE_MESSAGES[e.code];
  if (e?.message) return e.message;
  return "We couldn't resume this matching session. Try again, or start a new matching session.";
}

interface MatchingSessionsPanelProps {
  cohortId: string;
  groupsPrereqsMet: boolean;
}

/**
 * Sessions list panel for the cohort Groups tab (MED-829 / E23-8 AC7, AC11, AC12).
 *
 * Always-expanded per OQ2 decision (most cohorts have 1-3 rows). Polls every
 * 30 s and refetches on window focus so an admin returning from the matching-
 * server SPA sees fresh state without manual refresh (AC11). After a Resume,
 * the next poll captures the row's transition to `in_progress` (AC12).
 */
export function MatchingSessionsPanel({
  cohortId,
  groupsPrereqsMet,
}: MatchingSessionsPanelProps) {
  const navigate = useNavigate();
  const [resumingId, setResumingId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['matchingSessions', cohortId],
    queryFn: () => matchingSessionsApi.list(cohortId),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  // Pre-flight readiness — used to gate the Start Matching button and to
  // surface a "what's blocking" alert above the panel. Polls on the same
  // cadence as the sessions list so admins see updates as they fix things.
  const { data: readiness } = useQuery({
    queryKey: ['matchingReadiness', cohortId],
    queryFn: () => matchingSessionsApi.readiness(cohortId),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const handleResume = async (sessionId: string) => {
    // Same new-tab pattern as StartMatchingButton: open a placeholder tab
    // synchronously to avoid popup blockers, then navigate it once the API
    // resolves. Falls back to in-tab nav if blocked.
    const newTab = window.open('about:blank', '_blank');
    if (newTab) newTab.opener = null;

    setResumingId(sessionId);
    try {
      const response = await matchingSessionsApi.resume(sessionId);
      if (newTab && !newTab.closed) {
        newTab.location.href = response.matching_url;
      } else {
        window.location.href = response.matching_url;
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      notifications.show({
        color: 'red',
        title: 'Could not resume',
        message: deriveResumeErrorMessage(err),
      });
      setResumingId(null);
      // Refetch so a stale row that was actually expired/failed gets corrected.
      refetch();
    }
  };

  const showReadinessAlert = readiness !== undefined && !readiness.ready;

  return (
    <Stack gap="md">
      {/* Readiness alert — surfaces the same blockers the Start Matching
          button is disabled on, so admins see what to fix without hovering. */}
      {showReadinessAlert && (
        <Alert
          icon={<AlertTriangle size={18} />}
          color="warningAmber"
          variant="light"
          title="Matching isn't ready yet"
        >
          <Stack gap="xs">
            <Text size="sm">
              Address the following before starting matching for this cohort:
            </Text>
            <List size="sm" spacing={4}>
              {readiness.blocking_reasons.map((reason) => (
                <List.Item key={reason.code}>{reason.message}</List.Item>
              ))}
            </List>
          </Stack>
        </Alert>
      )}

      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={4}>Matching Sessions</Title>
            <Group gap="sm">
              <SetUpMatchingButton
                cohortId={cohortId}
                groupsPrereqsMet={groupsPrereqsMet}
                readiness={readiness}
              />
              <StartMatchingButton
                cohortId={cohortId}
                groupsPrereqsMet={groupsPrereqsMet}
                readiness={readiness}
              />
            </Group>
          </Group>

          {isLoading && (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          )}

          {isError && (
            <Alert
              icon={<AlertCircle size={18} />}
              color="red"
              variant="light"
              title="Couldn't load sessions"
            >
              <Text size="sm">
                We couldn't load the matching sessions list. The list will refresh automatically.
              </Text>
            </Alert>
          )}

          {!isLoading && !isError && data && data.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No matching sessions yet — click Start Matching to begin.
            </Text>
          )}

          {!isLoading && !isError && data && data.length > 0 && (
            <Stack gap="sm">
              {data.map((session) => (
                <MatchingSessionRow
                  key={session.id}
                  session={session}
                  isResuming={resumingId === session.id}
                  onResume={handleResume}
                  onContinue={(sessionId) =>
                    navigate(`/cohorts/${cohortId}/matching/${sessionId}`)
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
